import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import useAppStore from '../../store/appStore';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';
import PePapersStorageHint, { PE_PAPERS_STORAGE_HINTS } from './PePapersStorageHint';
import {
    GOBD_CONFIRMATION_TEXT,
    GOBD_DOC_ID,
    GOBD_DOC_SUBTITLE,
    GOBD_DOC_TITLE,
    GOBD_KASSENSYSTEM_PRESETS,
    GOBD_LEGAL_BASIS_LINE,
    GOBD_PAGE2_BAND_LABEL,
    GOBD_SECTION_4_HINT,
    GOBD_SECTION_6_TEXT,
    GOBD_SECTION_TITLES,
    GOBD_TERMINAL_PRESETS,
    formatGobdEuro,
    formatGobdGutscheine,
    formatGobdTseTyp,
    formatGobdZBon,
    getDefaultGobdFormData,
    type GobdFormData,
    type GobdGutscheinTyp,
    type GobdTseTyp,
    type GobdZBonRhythmus,
} from '../../utils/pe-papers/gobdCatalog';

export interface GobdModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

const INPUT_CLASS =
    'w-full bg-pe-input dark:bg-black border border-pe-border dark:border-slate-700 rounded-lg px-3 py-2.5 text-pe-text dark:text-white text-sm focus:border-[#E6C299] outline-none';
const LABEL_CLASS =
    'text-[10px] font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';
const ERROR_RING = 'border-red-500 ring-1 ring-red-500/40';

const A4_PAGE_CLASS =
    'gobd-a4-page bg-white text-black mx-auto w-full max-w-[210mm] min-h-[297mm] h-auto shadow-xl rounded-sm text-left block overflow-visible box-border my-4 print:my-0 print:shadow-none print:rounded-none';

const STEP_LABELS: Record<number, string> = {
    1: 'Datenerfassung',
    2: 'Dokument & Archiv',
};

const GOBD_DOC_CSS = `
  .gobd-a4-page {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #0f172a !important;
    background: #ffffff !important;
    box-sizing: border-box !important;
    padding: 10mm 11mm !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .gobd-a4-page-2 { padding: 10mm 11mm !important; }
  .gobd-screen-only { display: block; }
  .gobd-page-label {
    font-size: 12.5px !important; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
    color: #64748b; margin: 0 0 10px; padding-bottom: 5px; border-bottom: 1px dashed #cbd5e1;
  }
  .gobd-page2-band {
    display: table; width: 100%; border-collapse: collapse;
    margin: 0 0 12px; padding: 0 0 8px;
    border-bottom: 1.5px solid #E6C299;
  }
  .gobd-page2-band td {
    vertical-align: middle; padding: 0; border: none; background: transparent;
    font-size: 12.5px !important; color: #475569; line-height: 1.35;
  }
  .gobd-page2-band td:last-child { text-align: right; white-space: nowrap; }
  .gobd-page2-band strong { color: #0f172a; font-weight: 700; }
  .gobd-header {
    width: 100%; border-collapse: collapse;
    margin: 0 0 14px; padding: 0 0 12px;
    border-bottom: 2px solid #E6C299;
  }
  .gobd-header td { vertical-align: top; padding: 0; border: none; background: transparent; }
  .gobd-title {
    font-size: 16px !important; font-weight: 800 !important; line-height: 1.25 !important;
    margin: 0 0 4px !important; text-align: left !important; color: #0f172a !important;
    text-transform: none !important; letter-spacing: 0 !important;
  }
  .gobd-title-pe { font-style: italic; color: #E6C299 !important; font-weight: 800; }
  .gobd-title-sub {
    display: block; font-size: 13px !important; font-weight: 700 !important;
    color: #0f172a !important; margin-top: 3px;
  }
  .gobd-salon-line { font-size: 12px !important; font-weight: 600; color: #334155; margin: 4px 0 0; }
  .gobd-header-meta {
    width: 180px; text-align: right; font-size: 11px !important; color: #475569; line-height: 1.4;
    white-space: nowrap; padding-left: 12px !important;
  }
  .gobd-header-meta p { margin: 0 0 4px !important; text-align: right !important; font-size: 11px !important; }
  .gobd-header-meta strong { color: #0f172a; font-weight: 700; }
  .gobd-lead {
    font-size: 11px !important; color: #475569; margin: 0 0 14px; line-height: 1.45; text-align: justify;
  }
  .gobd-box {
    border: 1px solid #cbd5e1 !important;
    border-radius: 6px !important;
    background: #f1f5f9 !important;
    padding: 12px 14px !important;
    margin: 0 0 14px !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .gobd-h {
    font-size: 12px !important; font-weight: 800 !important; text-transform: uppercase;
    letter-spacing: 0.04em; border-bottom: 1.5px solid #E6C299; padding-bottom: 5px;
    margin: 0 0 10px !important; color: #0f172a !important; text-align: left !important;
    background: transparent !important;
  }
  .gobd-row { margin: 0 0 7px !important; font-size: 12px !important; color: #1e293b !important; line-height: 1.4; }
  .gobd-row strong { color: #0f172a !important; font-weight: 700; }
  .gobd-legal {
    font-size: 11px !important; text-align: justify; margin: 0 0 8px !important;
    color: #334155 !important; line-height: 1.45 !important;
  }
  .gobd-sig-wrap {
    margin-top: 12px;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  .gobd-sig-grid {
    display: table !important; width: 100% !important; table-layout: fixed !important;
    margin-top: 12px !important; border-collapse: separate !important; border-spacing: 16px 0 !important;
  }
  .gobd-sig-col {
    display: table-cell !important; width: 50% !important; vertical-align: top !important;
    padding: 0 !important;
  }
  .gobd-sig-label {
    margin: 0 0 6px !important; font-size: 11px !important; font-weight: 700 !important;
    color: #0f172a !important; text-transform: uppercase; letter-spacing: 0.03em;
  }
  .gobd-sig-value {
    margin: 0 0 8px !important; font-size: 12px !important; color: #1e293b !important;
  }
  .gobd-sig-line {
    border-bottom: 1.5px solid #0f172a !important;
    height: 36px !important;
    margin: 0 0 8px !important;
    width: 100% !important;
  }
  .gobd-stamp-box {
    border: none !important;
    border-bottom: 2px solid #1e293b !important;
    background: #ffffff !important;
    height: 56px !important;
    margin: 8px 0 0 !important;
    border-radius: 0 !important;
  }
  .gobd-sig-name {
    margin: 0 !important; font-size: 11px !important; color: #334155 !important;
    font-weight: 600 !important;
  }
`;

const GOBD_PDF_CSS = `
  ${GOBD_DOC_CSS}
  .gobd-screen-only { display: none !important; }
  .gobd-a4-page, .gobd-a4-page-2 {
    min-height: 0 !important; margin: 0 !important; padding: 0 !important;
    box-shadow: none !important; max-width: none !important; border-radius: 0 !important;
  }
`;

const GOBD_PRINT_CSS = `
  ${GOBD_PDF_CSS}
  @page { size: A4; margin: 10mm; }
  html, body {
    margin: 0 !important; padding: 0 !important; background: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .gobd-a4-page, .gobd-a4-page-2 {
    padding: 0 !important; margin: 0 !important; min-height: 0 !important;
    height: auto !important; overflow: visible !important; box-shadow: none !important;
  }
  .print-break, .pepapers-print-break {
    page-break-before: always !important; break-before: page !important;
  }
  .gobd-box, .gobd-header, .gobd-sig-wrap, .gobd-page2-band {
    page-break-inside: avoid !important; break-inside: avoid !important;
  }
  .gobd-screen-only { display: none !important; }
`;

const isEmpty = (v: string) => !String(v || '').trim();

const formatGermanDate = (iso: string) => {
    if (!iso) return '____________';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return `${d}.${m}.${y}`;
};

const presetOrOther = (value: string, presets: readonly string[]): string => {
    const v = String(value || '').trim();
    if (!v) return '';
    if ((presets as readonly string[]).includes(v)) return v;
    return 'Sonstiges';
};

const GobdModal: React.FC<GobdModalProps> = ({ isOpen, onClose, initialData, onArchived }) => {
    const clientData = useAppStore((s: any) => s.clientData);
    const [currentStep, setCurrentStep] = useState(1);
    const [form, setForm] = useState<GobdFormData>(() => getDefaultGobdFormData());
    const [kassePreset, setKassePreset] = useState('');
    const [kasseOther, setKasseOther] = useState('');
    const [terminalPreset, setTerminalPreset] = useState('');
    const [terminalOther, setTerminalOther] = useState('');
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>(
        'idle'
    );
    const [archiveErrorDetail, setArchiveErrorDetail] = useState('');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);
    const printContainerRef = useRef<HTMLDivElement>(null);

    const isActionLocked = !disclaimerAccepted;

    useEffect(() => {
        if (!isOpen) return;
        const scrollY = window.scrollY;
        const prev = {
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width,
            htmlOverflow: document.documentElement.style.overflow,
        };
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        return () => {
            document.documentElement.style.overflow = prev.htmlOverflow;
            document.body.style.overflow = prev.overflow;
            document.body.style.position = prev.position;
            document.body.style.top = prev.top;
            document.body.style.width = prev.width;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setInvalidFields(new Set());
            setDisclaimerAccepted(false);
            setIsSaving(false);
            setArchiveFeedback('idle');
            setArchiveErrorDetail('');
            setArchiveDocId(null);
            return;
        }

        const base = getDefaultGobdFormData({
            salonName: clientData?.salonname || clientData?.firmenname || '',
            inhaberName: clientData?.unternehmername || '',
        });
        setForm(base);
        setKassePreset('');
        setKasseOther('');
        setTerminalPreset('');
        setTerminalOther('');
        setCurrentStep(1);
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setArchiveFeedback('idle');
        setArchiveErrorDetail('');
        setArchiveDocId(initialData?.id || null);

        if (initialData) {
            const payload = initialData.formData || initialData.payload?.formData;
            if (payload && typeof payload === 'object') {
                const next: GobdFormData = {
                    ...base,
                    ...payload,
                    wechselgeldGrundbestand: Number(payload.wechselgeldGrundbestand) || 0,
                    barZahlungAktiv: Boolean(payload.barZahlungAktiv),
                    gutscheinTypen: Array.isArray(payload.gutscheinTypen)
                        ? payload.gutscheinTypen.filter(
                              (t: string) => t === 'einzweck' || t === 'mehrzweck'
                          )
                        : [],
                    tseTyp:
                        payload.tseTyp === 'cloud' || payload.tseTyp === 'hardware'
                            ? payload.tseTyp
                            : '',
                    zBonRhythmus:
                        payload.zBonRhythmus === 'taeglich' ||
                        payload.zBonRhythmus === 'schichtbezogen'
                            ? payload.zBonRhythmus
                            : 'taeglich',
                };
                setForm(next);
                const kp = presetOrOther(next.kassenSystemName, GOBD_KASSENSYSTEM_PRESETS);
                setKassePreset(kp);
                setKasseOther(kp === 'Sonstiges' ? next.kassenSystemName : '');
                const tp = presetOrOther(next.unbarZahlungTerminal, GOBD_TERMINAL_PRESETS);
                setTerminalPreset(tp);
                setTerminalOther(tp === 'Sonstiges' ? next.unbarZahlungTerminal : '');
            }
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) setCurrentStep(step);
            if (initialData.confirmedDisclaimer) setDisclaimerAccepted(true);
        }
    }, [isOpen, clientData, initialData]);

    const patch = (partial: Partial<GobdFormData>) => {
        setForm((prev) => ({ ...prev, ...partial }));
    };

    const applyKassePreset = (preset: string) => {
        setKassePreset(preset);
        if (preset === 'Sonstiges') {
            patch({ kassenSystemName: kasseOther.trim() });
        } else {
            setKasseOther('');
            patch({ kassenSystemName: preset });
        }
    };

    const applyTerminalPreset = (preset: string) => {
        setTerminalPreset(preset);
        if (preset === 'Sonstiges') {
            patch({ unbarZahlungTerminal: terminalOther.trim() });
        } else {
            setTerminalOther('');
            patch({ unbarZahlungTerminal: preset });
        }
    };

    const toggleGutschein = (typ: GobdGutscheinTyp) => {
        setForm((prev) => {
            const has = prev.gutscheinTypen.includes(typ);
            return {
                ...prev,
                gutscheinTypen: has
                    ? prev.gutscheinTypen.filter((t) => t !== typ)
                    : [...prev.gutscheinTypen, typ],
            };
        });
    };

    const fieldClass = (id: string) =>
        `${INPUT_CLASS} ${invalidFields.has(id) ? ERROR_RING : ''}`;

    const validateStep1 = (): string[] => {
        const err: string[] = [];
        if (isEmpty(form.salonName)) err.push('salonName');
        if (isEmpty(form.inhaberName)) err.push('inhaberName');
        if (isEmpty(form.kassenSystemName)) err.push('kassenSystemName');
        if (isEmpty(form.softwareVersion)) err.push('softwareVersion');
        if (!form.tseTyp) err.push('tseTyp');
        if (isEmpty(form.tseSeriennummer)) err.push('tseSeriennummer');
        if (!form.zBonRhythmus) err.push('zBonRhythmus');
        if (!(Number(form.wechselgeldGrundbestand) >= 0)) err.push('wechselgeldGrundbestand');
        if (isEmpty(form.unbarZahlungTerminal)) err.push('unbarZahlungTerminal');
        if (isEmpty(form.notfallProzedere)) err.push('notfallProzedere');
        if (isEmpty(form.datumErstellung)) err.push('datumErstellung');
        if (isEmpty(form.versionsNummer)) err.push('versionsNummer');
        return err;
    };

    const handleWeiter = () => {
        const err = validateStep1();
        setInvalidFields(new Set(err));
        if (err.length) return;
        setCurrentStep(2);
    };

    const handleClose = () => onClose();

    const handlePrint = () => {
        if (isActionLocked) return;
        const iframe = document.createElement('iframe');
        iframe.style.cssText =
            'position:fixed;left:-12000px;top:0;width:210mm;height:297mm;border:none;visibility:hidden;';
        document.body.appendChild(iframe);
        const win = iframe.contentWindow;
        if (!win) {
            document.body.removeChild(iframe);
            return;
        }
        const html =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-gobd-print')?.innerHTML ||
            '';
        win.document.open();
        win.document.write(`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"/><title>${GOBD_DOC_TITLE}</title>
<style>${GOBD_PRINT_CSS}</style></head><body>${html}</body></html>`);
        win.document.close();
        setTimeout(() => {
            win.focus();
            win.print();
            setTimeout(() => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            }, 1000);
        }, 450);
    };

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-gobd-print')?.innerHTML ||
            '';
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = form.datumErstellung || new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `GoBD_Verfahrensdokumentation_${dateIso}.pdf`,
                extraCss: GOBD_PDF_CSS,
                splitPrintBreaks: true,
                scrubMode: 'minimal',
                fitSectionsToPage: true,
                letterheadOnBreaks: false,
            });
        } catch (err: any) {
            console.error('GoBD PDF fehlgeschlagen:', err);
            window.alert('PDF konnte nicht heruntergeladen werden.');
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;
        setIsSaving(true);
        setArchiveFeedback('idle');
        setArchiveErrorDetail('');
        const htmlContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-gobd-print')?.innerHTML ||
            '';
        try {
            const formClean = JSON.parse(JSON.stringify(form)) as GobdFormData;
            const htmlSafe =
                htmlContent && htmlContent.length < 850_000 ? htmlContent : '';
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'gobd-verfahrensdokumentation',
                status: asDraft ? 'entwurf' : 'final',
                salonName: form.salonName || 'Unbekannter Salon',
                mitarbeiterName: form.inhaberName || '',
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    formData: formClean,
                    documentId: GOBD_DOC_ID,
                    generatedHtml: htmlSafe,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) setTimeout(() => onClose(), 600);
            else setTimeout(() => setArchiveFeedback('idle'), 3000);
        } catch (e) {
            console.error('GoBD Archiv fehlgeschlagen:', e);
            setArchiveErrorDetail(e instanceof Error ? e.message : 'Unbekannter Fehler');
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const btnClass = (base: string) =>
        `${base} ${isActionLocked || isSaving ? 'opacity-50 cursor-not-allowed' : ''}`;

    const renderDocument = () => (
        <div
            ref={printContainerRef}
            id="pepapers-gobd-print"
            className="w-full h-auto overflow-visible"
        >
            <style>{GOBD_DOC_CSS}</style>

            <div className={A4_PAGE_CLASS}>
                <p className="gobd-screen-only gobd-page-label">Seite 1 von 2</p>
                <table className="gobd-header">
                    <tbody>
                        <tr>
                            <td>
                                <h1 className="gobd-title">
                                    <span className="gobd-title-pe">PE</span>Papers
                                    <span className="gobd-title-sub">GoBD-Verfahrensdokumentation</span>
                                </h1>
                                {form.salonName ? (
                                    <p className="gobd-salon-line">{form.salonName}</p>
                                ) : null}
                            </td>
                            <td className="gobd-header-meta">
                                <p>
                                    Dokument: <strong>{GOBD_DOC_ID}</strong>
                                </p>
                                <p>
                                    Version: <strong>{form.versionsNummer || '1.0'}</strong>
                                </p>
                                <p>
                                    Datum:{' '}
                                    <strong>{formatGermanDate(form.datumErstellung)}</strong>
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <p className="gobd-lead">
                    <strong>{GOBD_DOC_TITLE}</strong>
                    <br />
                    {GOBD_DOC_SUBTITLE}
                    <br />
                    {GOBD_LEGAL_BASIS_LINE}
                </p>

                <div className="gobd-box">
                    <h3 className="gobd-h">{GOBD_SECTION_TITLES.s1}</h3>
                    <p className="gobd-row">
                        <strong>Salon / Firma:</strong> {form.salonName || '—'}
                    </p>
                    <p className="gobd-row">
                        <strong>Inhaber/in:</strong> {form.inhaberName || '—'}
                    </p>
                    <p className="gobd-row">
                        <strong>Dokumentationsdatum:</strong>{' '}
                        {formatGermanDate(form.datumErstellung)}
                    </p>
                    <p className="gobd-row">
                        <strong>Versionsnummer:</strong> {form.versionsNummer || '1.0'}
                    </p>
                </div>

                <div className="gobd-box">
                    <h3 className="gobd-h">{GOBD_SECTION_TITLES.s2}</h3>
                    <p className="gobd-row">
                        <strong>Kassensystem:</strong> {form.kassenSystemName || '—'}
                    </p>
                    <p className="gobd-row">
                        <strong>Software-Version:</strong> {form.softwareVersion || '—'}
                    </p>
                    <p className="gobd-row">
                        <strong>TSE-Typ:</strong> {formatGobdTseTyp(form.tseTyp)}
                    </p>
                    <p className="gobd-row">
                        <strong>TSE-Seriennummer / Cert-ID:</strong>{' '}
                        {form.tseSeriennummer || '—'}
                    </p>
                    <p className="gobd-legal">
                        Das elektronische Aufzeichnungssystem ist mit einer zertifizierten technischen
                        Sicherheitseinrichtung (TSE) verbunden. Kassenvorgänge werden unveränderbar
                        protokolliert (§ 146a AO i. V. m. KassenSichV). Die TSE-Seriennummer bzw.
                        Zertifikats-ID ist im Kassensystem hinterlegt und bei Kontrollen vorzeigbar.
                    </p>
                </div>

                <div className="gobd-box">
                    <h3 className="gobd-h">{GOBD_SECTION_TITLES.s3}</h3>
                    <p className="gobd-row">
                        <strong>Z-Bon-Rhythmus:</strong> {formatGobdZBon(form.zBonRhythmus)}
                    </p>
                    <p className="gobd-row">
                        <strong>Wechselgeld-Grundbestand:</strong>{' '}
                        {formatGobdEuro(form.wechselgeldGrundbestand)} EUR
                    </p>
                    <p className="gobd-row">
                        <strong>Barzahlung:</strong>{' '}
                        {form.barZahlungAktiv ? 'aktiv / zugelassen' : 'nicht aktiv'}
                    </p>
                    <p className="gobd-legal">
                        Der Wechselgeld-Grundbestand wird bei Schichtbeginn gezählt und dokumentiert.
                        Tages- bzw. Schichtabschlüsse (Z-Bon) erfolgen im festgelegten Rhythmus und werden
                        aufbewahrt. Differenzen werden unverzüglich der Salonleitung gemeldet und im
                        Kassensystem bzw. Belegwesen nachvollziehbar festgehalten.
                    </p>
                    <p className="gobd-legal">
                        Barzahlungen werden ausschließlich über das dokumentierte elektronische
                        Aufzeichnungssystem erfasst. Private Entnahmen und Einlagen erfolgen nur mit
                        Eigenbeleg und klarer Kennzeichnung im System.
                    </p>
                </div>
            </div>

            <div className={`print-break pepapers-print-break gobd-a4-page-2 ${A4_PAGE_CLASS}`}>
                <p className="gobd-screen-only gobd-page-label">Seite 2 von 2</p>
                <table className="gobd-page2-band">
                    <tbody>
                        <tr>
                            <td>
                                <strong>{GOBD_DOC_ID}</strong>
                                {' · '}
                                Version {form.versionsNummer || '1.0'}
                            </td>
                            <td>{GOBD_PAGE2_BAND_LABEL}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="gobd-box">
                    <h3 className="gobd-h">{GOBD_SECTION_TITLES.s4}</h3>
                    <p className="gobd-row">
                        <strong>Kartenterminal / unbare Zahlungen:</strong>{' '}
                        {form.unbarZahlungTerminal || '—'}
                    </p>
                    <p className="gobd-row">
                        <strong>Gutscheine:</strong> {formatGobdGutscheine(form.gutscheinTypen)}
                    </p>
                    <p className="gobd-legal">{GOBD_SECTION_4_HINT}</p>
                </div>

                <div className="gobd-box">
                    <h3 className="gobd-h">{GOBD_SECTION_TITLES.s5}</h3>
                    <p className="gobd-legal">{form.notfallProzedere || '—'}</p>
                </div>

                <div className="gobd-box">
                    <h3 className="gobd-h">{GOBD_SECTION_TITLES.s6}</h3>
                    <p className="gobd-legal">{GOBD_SECTION_6_TEXT}</p>
                    <p className="gobd-row">
                        <strong>Aktuelle Version:</strong> {form.versionsNummer || '1.0'} ·{' '}
                        <strong>Stand:</strong> {formatGermanDate(form.datumErstellung)}
                    </p>
                </div>

                <div className="gobd-box gobd-sig-wrap">
                    <h3 className="gobd-h">Bestätigung &amp; Unterschrift</h3>
                    <p className="gobd-legal">{GOBD_CONFIRMATION_TEXT}</p>
                    <div className="gobd-sig-grid">
                        <div className="gobd-sig-col">
                            <p className="gobd-sig-label">Ort, Datum:</p>
                            <p className="gobd-sig-value">
                                {[String(clientData?.ort || '').trim(), formatGermanDate(form.datumErstellung)]
                                    .filter(Boolean)
                                    .join(' · ') || formatGermanDate(form.datumErstellung)}
                            </p>
                            <div className="gobd-sig-line" />
                        </div>
                        <div className="gobd-sig-col">
                            <p className="gobd-sig-label">Unterschrift Inhaber/in &amp; Firmenstempel:</p>
                            <div className="gobd-sig-line" />
                            <p className="gobd-sig-name">
                                {form.inhaberName || '____________'}
                            </p>
                            <div className="gobd-stamp-box" aria-hidden="true" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!isOpen) return null;

    const modalWidthClass = currentStep === 2 ? 'max-w-5xl' : 'max-w-2xl';

    return createPortal(
        <>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] rounded-lg p-2 border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8 leading-snug">
                                GoBD-Verfahrensdokumentation
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-5">
                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Salon- &amp; Inhaber-Stammdaten
                                    </p>
                                    <div>
                                        <label className={LABEL_CLASS}>Salon / Firma</label>
                                        <input
                                            className={fieldClass('salonName')}
                                            value={form.salonName}
                                            onChange={(e) => patch({ salonName: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Inhaber/in</label>
                                        <input
                                            className={fieldClass('inhaberName')}
                                            value={form.inhaberName}
                                            onChange={(e) => patch({ inhaberName: e.target.value })}
                                        />
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Kassen-Hardware &amp; Software
                                    </p>
                                    <div>
                                        <label className={LABEL_CLASS}>Kassensystem</label>
                                        <select
                                            className={fieldClass('kassenSystemName')}
                                            value={kassePreset}
                                            onChange={(e) => applyKassePreset(e.target.value)}
                                        >
                                            <option value="">— bitte wählen —</option>
                                            {GOBD_KASSENSYSTEM_PRESETS.map((p) => (
                                                <option key={p} value={p}>
                                                    {p}
                                                </option>
                                            ))}
                                        </select>
                                        {kassePreset === 'Sonstiges' ? (
                                            <input
                                                className={`${fieldClass('kassenSystemName')} mt-2`}
                                                placeholder="Kassensystem (Freitext)"
                                                value={kasseOther}
                                                onChange={(e) => {
                                                    setKasseOther(e.target.value);
                                                    patch({ kassenSystemName: e.target.value });
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Software-Version</label>
                                        <input
                                            className={fieldClass('softwareVersion')}
                                            value={form.softwareVersion}
                                            onChange={(e) =>
                                                patch({ softwareVersion: e.target.value })
                                            }
                                            placeholder="z. B. 4.12.3"
                                        />
                                    </div>
                                    <div>
                                        <p className={LABEL_CLASS}>TSE-Typ</p>
                                        <div
                                            className={`space-y-2 rounded-lg border px-3 py-3 ${
                                                invalidFields.has('tseTyp')
                                                    ? 'border-red-500'
                                                    : 'border-pe-border dark:border-slate-700'
                                            }`}
                                        >
                                            {([
                                                ['cloud', 'Cloud-TSE'],
                                                ['hardware', 'Hardware-TSE'],
                                            ] as Array<[GobdTseTyp, string]>).map(([id, label]) => (
                                                <label
                                                    key={id}
                                                    className="flex items-center gap-2 cursor-pointer text-sm text-pe-text dark:text-slate-200"
                                                >
                                                    <input
                                                        type="radio"
                                                        name="gobd-tse"
                                                        checked={form.tseTyp === id}
                                                        onChange={() => patch({ tseTyp: id })}
                                                    />
                                                    {label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>TSE-Seriennummer / Cert-ID</label>
                                        <input
                                            className={fieldClass('tseSeriennummer')}
                                            value={form.tseSeriennummer}
                                            onChange={(e) =>
                                                patch({ tseSeriennummer: e.target.value })
                                            }
                                        />
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Kassenpraxis &amp; Finanzen
                                    </p>
                                    <div>
                                        <p className={LABEL_CLASS}>Z-Bon-Rhythmus</p>
                                        <div
                                            className={`space-y-2 rounded-lg border px-3 py-3 ${
                                                invalidFields.has('zBonRhythmus')
                                                    ? 'border-red-500'
                                                    : 'border-pe-border dark:border-slate-700'
                                            }`}
                                        >
                                            {([
                                                ['taeglich', 'Täglich'],
                                                ['schichtbezogen', 'Schichtbezogen'],
                                            ] as Array<[GobdZBonRhythmus, string]>).map(
                                                ([id, label]) => (
                                                    <label
                                                        key={id}
                                                        className="flex items-center gap-2 cursor-pointer text-sm text-pe-text dark:text-slate-200"
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="gobd-zbon"
                                                            checked={form.zBonRhythmus === id}
                                                            onChange={() =>
                                                                patch({ zBonRhythmus: id })
                                                            }
                                                        />
                                                        {label}
                                                    </label>
                                                )
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>
                                            Wechselgeld-Grundbestand (EUR)
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            step={0.01}
                                            className={fieldClass('wechselgeldGrundbestand')}
                                            value={
                                                Number.isFinite(form.wechselgeldGrundbestand)
                                                    ? form.wechselgeldGrundbestand
                                                    : 0
                                            }
                                            onChange={(e) =>
                                                patch({
                                                    wechselgeldGrundbestand:
                                                        Number(e.target.value) || 0,
                                                })
                                            }
                                        />
                                    </div>
                                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={form.barZahlungAktiv}
                                            onChange={(e) =>
                                                patch({ barZahlungAktiv: e.target.checked })
                                            }
                                            className="w-5 h-5 shrink-0 cursor-pointer appearance-none bg-pe-input dark:bg-black border-2 border-[#E6C299]/60 rounded-md checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors after:content-['✔'] after:absolute after:text-black after:text-[11px] after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                                        />
                                        <span className="text-xs font-bold uppercase tracking-wider text-pe-text dark:text-slate-200">
                                            Barzahlung aktiv
                                        </span>
                                    </label>
                                    <div>
                                        <label className={LABEL_CLASS}>Kartenterminal</label>
                                        <select
                                            className={fieldClass('unbarZahlungTerminal')}
                                            value={terminalPreset}
                                            onChange={(e) => applyTerminalPreset(e.target.value)}
                                        >
                                            <option value="">— bitte wählen —</option>
                                            {GOBD_TERMINAL_PRESETS.map((p) => (
                                                <option key={p} value={p}>
                                                    {p}
                                                </option>
                                            ))}
                                        </select>
                                        {terminalPreset === 'Sonstiges' ? (
                                            <input
                                                className={`${fieldClass('unbarZahlungTerminal')} mt-2`}
                                                placeholder="Terminal (Freitext)"
                                                value={terminalOther}
                                                onChange={(e) => {
                                                    setTerminalOther(e.target.value);
                                                    patch({ unbarZahlungTerminal: e.target.value });
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                    <div>
                                        <p className={LABEL_CLASS}>Gutscheine</p>
                                        <div className="space-y-2 rounded-lg border border-pe-border dark:border-slate-700 px-3 py-3">
                                            {(
                                                [
                                                    ['einzweck', 'Einzweck-Gutscheine'],
                                                    ['mehrzweck', 'Mehrzweck-Gutscheine'],
                                                ] as Array<[GobdGutscheinTyp, string]>
                                            ).map(([id, label]) => (
                                                <label
                                                    key={id}
                                                    className="flex items-center gap-2 cursor-pointer text-sm text-pe-text dark:text-slate-200"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={form.gutscheinTypen.includes(id)}
                                                        onChange={() => toggleGutschein(id)}
                                                    />
                                                    {label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                                        Notfall &amp; Dokumentation
                                    </p>
                                    <div>
                                        <label className={LABEL_CLASS}>Notfallprozedere</label>
                                        <textarea
                                            className={`${fieldClass('notfallProzedere')} min-h-[96px]`}
                                            value={form.notfallProzedere}
                                            onChange={(e) =>
                                                patch({ notfallProzedere: e.target.value })
                                            }
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className={LABEL_CLASS}>Datum der Dokumentation</label>
                                            <input
                                                type="date"
                                                className={fieldClass('datumErstellung')}
                                                value={form.datumErstellung}
                                                onChange={(e) =>
                                                    patch({ datumErstellung: e.target.value })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Versionsnummer</label>
                                            <input
                                                className={fieldClass('versionsNummer')}
                                                value={form.versionsNummer}
                                                onChange={(e) =>
                                                    patch({ versionsNummer: e.target.value })
                                                }
                                                placeholder="1.0"
                                            />
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="w-full overflow-visible pr-1 pb-8">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-pe-muted dark:text-slate-500 mb-3 print:hidden">
                                    Vorschau — 2 A4-Seiten
                                </p>
                                {renderDocument()}
                            </div>
                        )}
                    </div>

                    {currentStep === 2 && (
                        <div className="px-4 sm:px-6 py-3 shrink-0 print:hidden border-t border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#111] relative z-10">
                            <div
                                className={`rounded-xl border-2 transition-all duration-200 ${
                                    disclaimerAccepted
                                        ? 'border-emerald-500/40 bg-emerald-500/5 px-3 py-2.5'
                                        : 'border-amber-500 bg-amber-500/15 dark:bg-amber-500/10 shadow-[0_0_24px_rgba(245,158,11,0.25)] p-3 sm:p-4'
                                }`}
                            >
                                {!disclaimerAccepted && (
                                    <>
                                        <p className="mb-1 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-amber-800 dark:text-amber-300">
                                            Pflicht · Rechtlicher Hinweis bestätigen
                                        </p>
                                        <p className="mb-2 text-[11px] sm:text-xs font-semibold text-amber-950 dark:text-amber-100/95 leading-snug">
                                            Ohne diese Bestätigung bleiben PDF-Download, Druck und
                                            Archiv-Speichern gesperrt.
                                        </p>
                                    </>
                                )}
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={disclaimerAccepted}
                                        onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                        className="mt-0.5 w-7 h-7 sm:w-6 sm:h-6 shrink-0 cursor-pointer appearance-none bg-pe-input dark:bg-black border-2 border-amber-600 dark:border-amber-400 rounded-md checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors after:content-['✔'] after:absolute after:text-black after:text-[14px] after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
                                    />
                                    <span
                                        className={
                                            disclaimerAccepted
                                                ? 'text-sm font-semibold text-emerald-800 dark:text-emerald-300 leading-snug'
                                                : 'text-sm text-pe-text dark:text-slate-200 leading-snug'
                                        }
                                    >
                                        {disclaimerAccepted
                                            ? 'Rechtlicher Hinweis bestätigt'
                                            : 'Ich habe die Daten geprüft. Es handelt sich um ein Standard-Muster ohne individuelle Rechts-/Steuerberatung; Verwendung auf eigene Verantwortung.'}
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="px-6 pt-3 print:hidden">
                            <PePapersStorageHint
                                hint={PE_PAPERS_STORAGE_HINTS.gobd}
                                variant="modal"
                            />
                        </div>
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        {currentStep === 1 && invalidFields.size > 0 && (
                            <p className="mb-3 text-center text-xs font-semibold text-red-400">
                                Bitte fülle die markierten Pflichtfelder aus.
                            </p>
                        )}
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300"
                                >
                                    Zurück zur Bearbeitung
                                </button>
                            )}
                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft'
                                            ? 'Gespeichert! ✓'
                                            : isSaving
                                              ? 'Speichert...'
                                              : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] text-black"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299]'
                                        )}
                                    >
                                        {archiveFeedback === 'success'
                                            ? 'Gespeichert! ✓'
                                            : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] text-black'
                                        )}
                                    >
                                        {isPdfDownloading
                                            ? 'PDF wird erstellt...'
                                            : 'Als PDF speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrint}
                                        disabled={isActionLocked}
                                        className={btnClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299]'
                                        )}
                                    >
                                        Drucken
                                    </button>
                                </>
                            )}
                        </div>
                        {archiveFeedback === 'error' && (
                            <p className="text-[10px] text-red-400 mt-2 text-center">
                                Archivierung fehlgeschlagen. Bitte erneut versuchen.
                                {archiveErrorDetail ? (
                                    <>
                                        <br />
                                        <span className="opacity-80">{archiveErrorDetail}</span>
                                    </>
                                ) : null}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default GobdModal;
